import React, { useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import Sidebar from "../components/MasterSideBar";
import TopBar from "../components/TopbarNav";
import LeaveRequestForm from "../components/LeaveRequestForm";
import { FiCalendar, FiClockOff } from "react-icons/fi";
import { Bar } from "react-chartjs-2";
import { CSVLink } from "react-csv";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export const ProtectedRoute = ({ children }) => {
    const token = localStorage.getItem("token");
    return token ? children : <Navigate to="/" />;
};

export default function LeaveDashboardPage() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [leaveBalances, setLeaveBalances] = useState([]);
    const [pendingLeaves, setPendingLeaves] = useState([]);
    const [leaveHistory, setLeaveHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);

    // Filters and UI state
    const [filterStatus, setFilterStatus] = useState("All");
    const [filterType, setFilterType] = useState("All");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(8);
    const [chartView, setChartView] = useState("monthly"); // monthly/yearly
    const [showLeaveForm, setShowLeaveForm] = useState(false);

    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user"));
    const staffId = user ? user.id : null;

    const handleLogout = () => {
        localStorage.removeItem("token");
        window.location.href = "/";
    };

    // Fetch balances and pending leaves
    useEffect(() => {
        const fetchLeaveData = async () => {
            setLoading(true);
            try {
                if (!token) return;
                const headers = { Authorization: `Bearer ${token}` };

                const [balancesRes, pendingRes] = await Promise.all([
                    fetch(
                        "https://ims.holdthechild.net/api/staff/leave-balances",
                        { headers }
                    ),
                    fetch(
                        "https://ims.holdthechild.net/api/pending-leave-requests",
                        { headers }
                    ),
                ]);

                if (!balancesRes.ok || !pendingRes.ok) {
                    throw new Error("Failed to fetch leave data");
                }

                const balancesData = await balancesRes.json();
                const pendingData = await pendingRes.json();

                setLeaveBalances(Array.isArray(balancesData) ? balancesData : []);
                setPendingLeaves(Array.isArray(pendingData) ? pendingData : []);
            } catch (error) {
                console.error("Error fetching leave dashboard data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchLeaveData();
    }, [token]);

    // Fetch leave history for charts and history table
    useEffect(() => {
        const fetchLeaveHistory = async () => {
            setHistoryLoading(true);
            setHistoryError(null);
            try {
                if (!token) return;
                const headers = { Authorization: `Bearer ${token}` };

                // Make sure your backend supports this endpoint and returns
                // leave history records with fields:
                // id, type, startDate, endDate, days, status (capitalized or normalized here)
                const historyRes = await fetch(
                    "https://ims.holdthechild.net/api/staff/leave-history",
                    { headers }
                );

                if (!historyRes.ok) throw new Error("Failed to fetch leave history");

                const historyData = await historyRes.json();

                // Normalize status (capitalize first letter) if needed
                const normalized = historyData.map((item) => ({
                    ...item,
                    status:
                        item.status?.charAt(0).toUpperCase() + item.status?.slice(1).toLowerCase() ||
                        "Pending",
                    type: item.type || item.typeOfLeave || "Unknown",
                    days: item.days || 1,
                }));

                setLeaveHistory(normalized);
            } catch (error) {
                setHistoryError(error.message || "Error loading leave history");
            } finally {
                setHistoryLoading(false);
            }
        };

        fetchLeaveHistory();
    }, [token]);

    // Derived leave types from balances or history
    const leaveTypes = useMemo(() => {
        const typesSet = new Set();
        leaveBalances.forEach((b) => typesSet.add(b.type));
        leaveHistory.forEach((h) => typesSet.add(h.type));
        return Array.from(typesSet);
    }, [leaveBalances, leaveHistory]);

    // Filtering leave history based on status and type filters
    const filteredHistory = useMemo(() => {
        return leaveHistory.filter((leave) => {
            const statusMatch =
                filterStatus === "All" || leave.status === filterStatus;
            const typeMatch = filterType === "All" || leave.type === filterType;
            return statusMatch && typeMatch;
        });
    }, [leaveHistory, filterStatus, filterType]);

    // Pagination for history table
    const totalPages = Math.ceil(filteredHistory.length / pageSize);
    const paginatedHistory = filteredHistory.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    // Handlers for filters and pagination
    const handleFilterStatusChange = (e) => {
        setFilterStatus(e.target.value);
        setCurrentPage(1);
    };
    const handleFilterTypeChange = (e) => {
        setFilterType(e.target.value);
        setCurrentPage(1);
    };
    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    // Chart data generation (monthly or yearly)
    const chartData = useMemo(() => {
        if (filteredHistory.length === 0) return null;

        if (chartView === "monthly") {
            // Aggregate days by month-year, e.g. "Jan 2025"
            const months = {};
            filteredHistory.forEach(({ startDate, days }) => {
                const d = new Date(startDate);
                const key = d.toLocaleString("default", { month: "short", year: "numeric" });
                months[key] = (months[key] || 0) + days;
            });
            const labels = Object.keys(months).sort(
                (a, b) => new Date(a) - new Date(b)
            );
            const data = labels.map((label) => months[label]);
            return {
                labels,
                datasets: [
                    {
                        label: "Days Taken",
                        data,
                        backgroundColor: "rgba(59, 130, 246, 0.7)", // blue
                    },
                ],
            };
        } else {
            // yearly aggregation
            const years = {};
            filteredHistory.forEach(({ startDate, days }) => {
                const year = new Date(startDate).getFullYear();
                years[year] = (years[year] || 0) + days;
            });
            const labels = Object.keys(years).sort();
            const data = labels.map((y) => years[y]);
            return {
                labels,
                datasets: [
                    {
                        label: "Days Taken",
                        data,
                        backgroundColor: "rgba(59, 130, 246, 0.7)",
                    },
                ],
            };
        }
    }, [filteredHistory, chartView]);

    const chartOptions = {
        responsive: true,
        plugins: {
            legend: { position: "top" },
            title: {
                display: true,
                text:
                    chartView === "monthly"
                        ? "Leave Days Taken per Month"
                        : "Leave Days Taken per Year",
            },
        },
    };

    // Calculate total, used, remaining from balances
    // If your API returns usedDays and remainingDays per type, use those directly
    // Otherwise calculate from history + balances

    // For display: type, totalDays (used+remaining), used, remaining
    const balancesDisplay = leaveBalances.map(({ type, remainingDays, usedDays }) => {
        return {
            type,
            totalDays: remainingDays + usedDays,
            used: usedDays,
            remaining: remainingDays,
        };
    });

    // Status color map for badges
    const statusColors = {
        Approved: "bg-green-100 text-green-700",
        Rejected: "bg-red-100 text-red-700",
        Pending: "bg-yellow-100 text-yellow-700",
    };

    // CSV export headers for history
    const csvHeaders = [
        { label: "Type", key: "type" },
        { label: "Start Date", key: "startDate" },
        { label: "End Date", key: "endDate" },
        { label: "Days", key: "days" },
        { label: "Status", key: "status" },
    ];

    return (
      <div className="flex h-screen bg-gray-50 font-sans">
      {/* Controlled Sidebar (same pattern as ContractsPage) */}
      <Sidebar visible={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={handleLogout} />

      {/* Main area - offset on md screens */}
      <div className="flex flex-col flex-1 transition-all duration-300 md:ml-64">
        {/* TopBar must call onSidebarToggle when user taps the hamburger on mobile */}
        <TopBar onSidebarToggle={() => setSidebarOpen(true)} />

            <main className="flex-1 p-4 sm:p-6 overflow-y-auto md:ml-64">

                <h1 className="text-xl sm:text-2xl font-bold text-gray-700 mb-6 mt-16 sm:mt-20">
                    Leave Dashboard
                </h1>

                <button
                    onClick={() => setShowLeaveForm(true)}
                    className="mb-6 sm:mb-8 w-full sm:w-auto px-4 sm:px-6 py-2 bg-orange-500 text-white rounded-md shadow hover:bg-orange-600 transition"
                >
                    Request Leave
                </button>

                {showLeaveForm && staffId && token && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                        onClick={() => setShowLeaveForm(false)}
                    >
                        <div
                            className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 relative"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setShowLeaveForm(false)}
                                className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 text-3xl font-bold leading-none"
                            >
                                &times;
                            </button>
                            <LeaveRequestForm staffId={staffId} token={token} />
                        </div>
                    </div>
                )}

                {loading ? (
                    <p className="text-gray-600">Loading leave data...</p>
                ) : (
                    <>
                        {/* Leave Balances */}
                        <section className="mb-12 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {balancesDisplay.length === 0 ? (
                                <p className="text-gray-500 col-span-full">No leave balances available.</p>
                            ) : (
                                balancesDisplay.map(({ type, totalDays, used, remaining }) => (
                                    <div
                                        key={type}
                                        className="bg-white rounded-lg shadow p-6 flex flex-col"
                                    >
                                        <div className="flex items-center space-x-3 mb-4">
                                            <FiCalendar className="text-orange-500 text-4xl" />
                                            <h2 className="text-xl font-semibold text-gray-800">{type} Leave</h2>
                                        </div>
                                        <p>Total: <span className="font-semibold">{totalDays}</span> days</p>
                                        <p className="mt-1 text-red-600 font-semibold">Used: {used} days</p>
                                        <p className="mt-1 text-green-700 font-semibold">Remaining: {remaining} days</p>
                                    </div>
                                ))
                            )}
                        </section>

                        {/* Leave Usage Chart */}
                        <section className="mb-12">
                            <div className="flex justify-between items-center mb-5">
                                <h2 className="text-2xl font-semibold">Leave Usage Chart</h2>
                                <div className="space-x-3">
                                    <button
                                        onClick={() => setChartView("monthly")}
                                        className={`px-4 py-2 rounded ${chartView === "monthly"
                                            ? "bg-blue-600 text-white"
                                            : "bg-gray-200 text-gray-700"
                                            }`}
                                    >
                                        Monthly
                                    </button>
                                    <button
                                        onClick={() => setChartView("yearly")}
                                        className={`px-4 py-2 rounded ${chartView === "yearly"
                                            ? "bg-blue-600 text-white"
                                            : "bg-gray-200 text-gray-700"
                                            }`}
                                    >
                                        Yearly
                                    </button>
                                </div>
                            </div>
                            {historyLoading && <p>Loading chart data...</p>}
                            {historyError && <p className="text-red-600">{historyError}</p>}
                            {!historyLoading && !historyError && filteredHistory.length === 0 && (
                                <p>No leave data available for the selected filters.</p>
                            )}
                            {!historyLoading && !historyError && filteredHistory.length > 0 && (
                                <Bar data={chartData} options={chartOptions} />
                            )}
                        </section>

                        {/* Leave History */}
                        <section>
                            <h2 className="text-2xl font-semibold mb-5">Leave History</h2>

                            {/* Filters */}
                            <div className="flex flex-wrap gap-4 mb-4">
                                <div>
                                    <label className="block font-medium mb-1">Filter by Status</label>
                                    <select
                                        className="border border-gray-300 rounded px-3 py-2"
                                        value={filterStatus}
                                        onChange={handleFilterStatusChange}
                                    >
                                        <option value="All">All</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Rejected">Rejected</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block font-medium mb-1">Filter by Type</label>
                                    <select
                                        className="border border-gray-300 rounded px-3 py-2"
                                        value={filterType}
                                        onChange={handleFilterTypeChange}
                                    >
                                        <option value="All">All</option>
                                        {leaveTypes.map((lt) => (
                                            <option key={lt} value={lt}>
                                                {lt}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="ml-auto flex items-center space-x-4">
                                    <CSVLink
                                        data={filteredHistory}
                                        headers={csvHeaders}
                                        filename={"leave-history.csv"}
                                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                                    >
                                        Export CSV
                                    </CSVLink>
                                </div>
                            </div>

                            {/* Table */}
                            {historyLoading && <p>Loading leave history...</p>}
                            {historyError && <p className="text-red-600">{historyError}</p>}
                            {!historyLoading && !historyError && filteredHistory.length === 0 && (
                                <p>No leave records found.</p>
                            )}
                            {!historyLoading && !historyError && filteredHistory.length > 0 && (
                                <>
                                    <div className="hidden sm:block overflow-x-auto border rounded">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                                        Type
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                                        Start Date
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                                        End Date
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                                        Days
                                                    </th>
                                                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                                        Status
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {paginatedHistory.map(({ id, type, startDate, endDate, days, status }) => (
                                                    <tr
                                                        key={id}
                                                        className={`${status === "Approved"
                                                            ? "bg-green-50"
                                                            : status === "Rejected"
                                                                ? "bg-red-50"
                                                                : "bg-yellow-50"
                                                            }`}
                                                    >
                                                        <td className="px-4 py-2 whitespace-nowrap">{type}</td>
                                                        <td className="px-4 py-2 whitespace-nowrap">
                                                            {new Date(startDate).toLocaleDateString()}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap">
                                                            {new Date(endDate).toLocaleDateString()}
                                                        </td>
                                                        <td className="px-4 py-2 whitespace-nowrap">{days}</td>
                                                        <td className="px-4 py-2 whitespace-nowrap font-semibold">{status}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    {/* Mobile Card View */}
<div className="grid gap-4 sm:hidden">
  {paginatedHistory.map(({ id, type, startDate, endDate, days, status }) => (
    <div
      key={id}
      className="bg-white p-4 rounded shadow border"
    >
      <p><span className="font-semibold">Type:</span> {type}</p>
      <p><span className="font-semibold">Start:</span> {new Date(startDate).toLocaleDateString()}</p>
      <p><span className="font-semibold">End:</span> {new Date(endDate).toLocaleDateString()}</p>
      <p><span className="font-semibold">Days:</span> {days}</p>
      <p className={`font-semibold ${statusColors[status]}`}>{status}</p>
    </div>
  ))}
</div>

                                    {/* Pagination */}
                                    <div className="flex justify-between items-center mt-4">
                                        <button
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                                        >
                                            Prev
                                        </button>
                                        <span>
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <button
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50"
                                        >
                                            Next
                                        </button>
                                    </div>
                                </>
                            )}
                        </section>

                        {/* Pending Leaves */}
                        <section className="mt-12">
                            <h2 className="text-2xl font-semibold mb-5">Pending Leave Requests</h2>
                            {pendingLeaves.length === 0 ? (
                                <p>No pending leave requests.</p>
                            ) : (
                                <div className="overflow-x-auto border rounded">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                                    Type
                                                </th>
                                                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                                    Start Date
                                                </th>
                                                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                                    End Date
                                                </th>
                                                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                                    Days
                                                </th>
                                                <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">
                                                    Status
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {pendingLeaves.map(({ id, type, startDate, endDate, days, status }) => (
                                                <tr key={id} className="bg-yellow-50">
                                                    <td className="px-4 py-2 whitespace-nowrap">{type}</td>
                                                    <td className="px-4 py-2 whitespace-nowrap">
                                                        {new Date(startDate).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-4 py-2 whitespace-nowrap">
                                                        {new Date(endDate).toLocaleDateString()}
                                                    </td>
                                                    <td className="px-4 py-2 whitespace-nowrap">{days}</td>
                                                    <td className="px-4 py-2 whitespace-nowrap font-semibold">{status}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </section>
                    </>
                )}
            </main>
        </div>
        </div>
    );
}
